import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// redux slice
import { logOut, selectUserDetails, selectSelectedOrganization, selectOrganizationByInternalUser } from "../../features/auth/authSlice";
import { useLogoutMutation } from "../../features/auth/logoutApiSlice";
import { selectImpersonatedUser } from "../../features/admin/impersonated-user/impersonatedUserSlice";
import {
	selectLoanGuid,
	selectBorrowerName,
	selectVisitedStages,
	selectCurrentStage,
	selectSelectedPath,
	selectIncomeDocumentationType,
	selectSelectedProgram,
	selectQualifyInputs,
	selectDashboardDirty,
	setDashboardSaveRequest,
} from "../../features/current-loan/currentLoanSlice";
import { getQualifyEntryPath, isQualifyComplete } from "../../utils/qualify-entry-path";

// local storage utils
import { storageDelete } from "../../utils/localStorage";
import { VERBIAGE_CONSTANTS } from "../../utils/verbiage.constants";
import { markExplicitLogout, HOME_FROM_LOCATION } from "../../utils/logout-flag";

// mui components
import {
	Box,
	Divider,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	Collapse,
} from "@mui/material";

// custom components
import Heading5 from "../typography/heading-05";

// custom modal
import HelpCenterModal from "../modal/help-center-modal";
import ConfirmationModal from "../modal/confirmation-modal";

// mui icons
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import {
	HelpOutlineOutlined,
	ExpandLess,
	ExpandMore,
	CheckBox,
	CheckBoxOutlineBlank,
} from "@mui/icons-material";

// custom icons
import DashboardLogo from "../../assets/img/dashboard-logo.png";

// drawer data
import { drawerTopData, drawerBottomData } from "./drawer-data";

// helper utils
import {
	selectedUserWithLoanChannel,
	selectedUserWithAccessLevel,
} from "../../utils/common-helpers";
import {
	LOAN_CHANNEL_CONSTANTS,
	TPO_SECONDARY,
	BROKER_ADMIN,
	TPO_SECONDARY_USER_TITLE,
} from "../../utils/common-constants";

// custom styles
import styles from "./index.module.scss";

const CustomDrawer = ({
	drawerWidth,
	handleDrawerItemClick,
	mobileOpen,
	handleDrawerToggle,
}) => {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [currentPath, setCurrentPath] = useState("");
	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const organizationByInternalUser = useSelector(selectOrganizationByInternalUser);
	const [helpCenterOpened, setHelpCenterOpened] = useState(false);
	const [navGuardOpen, setNavGuardOpen] = useState(false);
	// PathA-2.11: leaving the Dashboard for Qualify/Submit with unsaved changes prompts a save.
	const [unsavedGuardPath, setUnsavedGuardPath] = useState(null);
	const dashboardDirty = useSelector(selectDashboardDirty);

	const loanGuid = useSelector(selectLoanGuid);
	const incomeDocumentationType = useSelector(selectIncomeDocumentationType);
	const borrowerName = useSelector(selectBorrowerName);
	const visitedStages = useSelector(selectVisitedStages);
	const currentStage = useSelector(selectCurrentStage);
	const selectedPath = useSelector(selectSelectedPath);
	const selectedProgram = useSelector(selectSelectedProgram);
	const qualifyInputs = useSelector(selectQualifyInputs);

	const [expandedItems, setExpandedItems] = useState({});
	const [logoutApi] = useLogoutMutation();

	useEffect(() => {
		let location = pathname.split("/");
		console.log("92 pathname", location[1]);
		setCurrentPath(location[1]);
	}, [pathname]);

	const isCurrentPath = (loanUrl) => {
		if (typeof loanUrl === "string") {
			return currentPath === loanUrl?.toLowerCase();
		} else {
			return loanUrl.some((url) => pathname.includes(url));
		}
	};

	const handleLogout = async () => {
		try {
			await logoutApi().unwrap();
		} catch (_) {
			// proceed with local logout even if API call fails
		}
		// set before dispatch: auth/logOut resets the whole Redux tree, so this
		// can't live in Redux state. Guards App.js's catch-all route in case it
		// renders once more with the pre-logout URL before navigate() below
		// takes effect, which would otherwise re-stash this page as `from`.
		markExplicitLogout();
		dispatch(logOut());
		storageDelete("access-token");
		storageDelete("refresh-token");
		navigate("/login", {
			replace: true,
			state: { from: HOME_FROM_LOCATION },
		});
	};

	const handleExpandClick = (itemTitle) => {
		setExpandedItems((prev) => ({
			...prev,
			[itemTitle]: !prev[itemTitle],
		}));
	};

	/* Quick-pricer External User */
	const isItemDisabled = (item) => {
		if (!userDetails) return true;

		const effectiveChannel = selectedUserWithLoanChannel(
			userDetails,
			impersonatedUser,
			selectedOrganization
		)?.loan_channel;

		const isWholesaleOrCB =
			effectiveChannel === LOAN_CHANNEL_CONSTANTS.WHS ||
			effectiveChannel === LOAN_CHANNEL_CONSTANTS.CB;

		if (userDetails?.snowFlakeOrganizations?.length > 0) {
			const enabledForInternalUsers = userDetails?.snowFlakeOrganizations?.find(
				(org) => org.company_name === organizationByInternalUser
			);

			if (item?.title === "Quick Pricer") {
				console.log("enabledForInternalUsers", enabledForInternalUsers);
				return !enabledForInternalUsers?.allow_quick_pricer;
			}
		} else if (userDetails?.organizations?.length > 0) {
			if (item?.title === "Quick Pricer") {
				console.log(
					"effectiveChannel Organization",
					userDetails?.organizations
				);

				const isCorrOrNdc =
					effectiveChannel === LOAN_CHANNEL_CONSTANTS.CORR ||
					effectiveChannel === LOAN_CHANNEL_CONSTANTS.NDC;
				const effectiveUserForAccess = selectedUserWithAccessLevel(
					userDetails,
					impersonatedUser
				);
				const accessLevel = effectiveUserForAccess?.access_level;
				const userTitle = effectiveUserForAccess?.user_title;
				const isTpoSecondaryOrBrokerAdmin =
					accessLevel === TPO_SECONDARY ||
					accessLevel === BROKER_ADMIN ||
					userTitle?.includes(TPO_SECONDARY_USER_TITLE);

				return !(isWholesaleOrCB || (isCorrOrNdc && isTpoSecondaryOrBrokerAdmin));
			}
		} else {
			if (item?.title === "Quick Pricer") {
				console.log(
					"No organization data available for userDetails",
					userDetails
				);
				return true;
			}
		}

		return false;
	};

	const shouldRenderItem = (item) => {
		if (!userDetails) return false;

		const isExecutiveOrManager =
			userDetails?.user_roles?.includes("executive") ||
			userDetails?.user_roles?.includes("manager") ||
			userDetails?.user_roles?.includes("AE") ||
			userDetails?.user_roles?.includes("AM") ||
			userDetails?.user_roles?.includes("PA") ||
			userDetails?.user_roles?.includes("assistant");

		const isTpoSecondary = userDetails?.access_level?.includes("TPO Secondary");

		const effectiveChannel = selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)?.loan_channel;

		const isWholesaleOrCB =
			effectiveChannel === LOAN_CHANNEL_CONSTANTS.WHS ||
			effectiveChannel === LOAN_CHANNEL_CONSTANTS.CB;

		if (item?.url === "new-loan") {
			// does not render for executive or manager
			return !(isExecutiveOrManager);
		}

		if (item?.url === "upload-history") {
			// follows same rules as new-loan
			return !(isExecutiveOrManager || isTpoSecondary);
		}

		if (item?.title === "Order Appraisal") {
			// does not render for either NDC or CORR users
			return !(
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
			);
		}

		if (item?.title === "Schedule Training") {
			// does not render for executive or manager
			return !isExecutiveOrManager;
		}

		if (item?.title === "Price my Loan") {
			// does not render for either NDC or CORR users
			return !(
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
				selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
					?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR
			);
		}

		return true;
	};

	const [pendingNavItem, setPendingNavItem] = useState(null);

	const handleNavWithGuard = (title, path) => {
		if (loanGuid && path && !path.startsWith("/current-loan")) {
			setPendingNavItem({ title, path });
			setNavGuardOpen(true);
		} else {
			handleDrawerItemClick(title, path);
		}
	};

	const handleGuardConfirm = () => {
		setNavGuardOpen(false);
		if (pendingNavItem) {
			handleDrawerItemClick(pendingNavItem.title, pendingNavItem.path);
		}
		setPendingNavItem(null);
	};

	const handleGuardCancel = () => {
		setNavGuardOpen(false);
		setPendingNavItem(null);
	};

	const CURRENT_LOAN_STEPS = [
		{
			key: "upload",
			label: "Upload",
			path: "/current-loan",
		},
		{
			key: "dashboard",
			label: "Dashboard",
			path: "/current-loan/dashboard",
		},
		{
			key: "qualify",
			label: "Qualify",
			// Sidebar's own "Qualify" link always stays within the Qualify flow itself
			// (never redirects to Submit, unlike Dashboard's generic "Continue" button —
			// Submit has its own separate sidebar item for that).
			path: getQualifyEntryPath({ selectedPath }),
			pathSelect: "/current-loan/qualify/path-select",
		},
		{
			key: "submit",
			label: "Submit",
			path: "/current-loan/submit",
		},
	];

	const handleStepNavigate = (step) => {
		const leavesDashboardForQualify =
			dashboardDirty &&
			pathname === "/current-loan/dashboard" &&
			(step.key === "qualify" || step.key === "submit");
		if (leavesDashboardForQualify) {
			setUnsavedGuardPath(step.path);
			return;
		}
		navigate(step.path);
	};

	// "Save changes": the Dashboard saves, then navigates (it owns the form state).
	const handleUnsavedSave = () => {
		dispatch(setDashboardSaveRequest({ target: unsavedGuardPath }));
		setUnsavedGuardPath(null);
	};

	const getStepStatus = (stepKey) => {
		if (currentStage === stepKey) return "active";
		if (visitedStages.includes(stepKey)) return "done";
		return "pending";
	};

	const drawer = (
		<Box className={styles.drawerDiv}>
			<Box className={styles.logoContainer}>
				<img src={DashboardLogo} alt="dashboard logo" />
			</Box>

			{/* Home + Add New Loan */}
			<List className={styles.drawerList}>
				{drawerTopData?.slice(0, 2).map((item, index) => {
					if (shouldRenderItem(item)) {
						const hasSubItems = item.subItems && item.subItems.length > 0;
						const isExpanded = expandedItems[item.title];
						const isSelected = isCurrentPath(item.url);

						return (
							<React.Fragment key={index}>
								<ListItem
									disablePadding
									className={
										isItemDisabled(item)
											? styles.drawerDisabledItem
											: isSelected
												? styles.drawerSelectedItem
												: styles.drawerItem
									}
								>
									<ListItemButton
										onClick={() => {
											if (!isItemDisabled(item)) {
												handleNavWithGuard(item.title, item.path);
											}
										}}
									>
										<ListItemIcon>{item.icon}</ListItemIcon>
										<Heading5
											text={item.title}
											fontType="semibold"
											extraClass={
												isSelected
													? styles.drawerSelectedText
													: styles.drawerText
											}
										/>
										{hasSubItems && (
											<Box
												sx={{ marginLeft: "auto", cursor: "pointer" }}
												onClick={(e) => {
													e.stopPropagation();
													handleExpandClick(item.title);
												}}
											>
												{isExpanded ? <ExpandLess /> : <ExpandMore />}
											</Box>
										)}
									</ListItemButton>
								</ListItem>
								{hasSubItems && (
									<Collapse in={isExpanded} timeout="auto" unmountOnExit>
										<List component="div" disablePadding>
											{item.subItems.map((subItem, subIndex) => (
												<ListItem
													key={`${index}-${subIndex}`}
													disablePadding
													className={
														isCurrentPath(subItem.url)
															? styles.drawerSelectedItem
															: styles.drawerItem
													}
													sx={{ pl: 4 }}
												>
													<ListItemButton
														onClick={() =>
															handleNavWithGuard(subItem.title, subItem.path)
														}
													>
														<ListItemIcon sx={{ minWidth: "32px" }}>
															{subItem.icon}
														</ListItemIcon>
														<Heading5
															text={subItem.title}
															fontType="semibold"
															extraClass={
																isCurrentPath(subItem.url)
																	? styles.drawerSelectedText
																	: styles.drawerText
															}
														/>
													</ListItemButton>
												</ListItem>
											))}
										</List>
									</Collapse>
								)}
							</React.Fragment>
						);
					}
					return null;
				})}
			</List>

			{/* CURRENT LOAN section — directly after Add New Loan */}
			{userDetails && (
				<>
					<Divider className={styles.dividerLine} />
					{/* Current Loan nav item + sub-steps in one list (no gap between them) */}
					<List className={styles.drawerList}>
						<ListItem
							disablePadding
							className={
								isCurrentPath(["current-loan"])
									? styles.drawerSelectedItem
									: styles.drawerItem
							}
						>
							
							<ListItemButton
								onClick={() =>
									handleNavWithGuard("Current Loan", "/current-loan")
								}
							>
								<ListItemIcon>
									<AutoAwesomeOutlinedIcon sx={{ fontSize: "32px" }} />
								</ListItemIcon>
								<Heading5
									text="Current Loan"
									fontType="semibold"
									extraClass={
										isCurrentPath(["current-loan"])
											? styles.drawerSelectedText
											: styles.drawerText
									}
								/>
							</ListItemButton>
						</ListItem>

						{/* Loan — first sub-item (done when all 4 steps are complete) */}
						{(() => {
							const allStepsDone = ["upload", "qualify", "dashboard", "submit"].every(
								(key) => visitedStages.includes(key)
							);
							const loanDone = allStepsDone;
							const loanActive = !loanDone;
							return (
								<ListItem
									disablePadding
									className={`${styles.clSubItem} ${loanDone ? styles.clSubItemDone : ""}`}
								>
									<ListItemButton
										onClick={() => navigate("/current-loan")}
										sx={{ pl: 4, pt: 1.5, pb: 1.5, pr: 1.5 }}
									>
										<ListItemIcon>
											{loanDone ? (
												<CheckBox className={styles.clIconDone} />
											) : (
												<CheckBoxOutlineBlank
													className={
														loanActive
															? styles.clIconActive
															: styles.clIconPending
													}
												/>
											)}
										</ListItemIcon>
										<Heading5
											text="Loan"
											fontType="semibold"
											extraClass={
												loanDone
													? styles.clStepTextDone
													: loanActive
													? styles.clStepTextActive
													: styles.clStepTextPending
											}
										/>
									</ListItemButton>
								</ListItem>
							);
						})()}

						{/* Upload / Qualify / Dashboard / Submit — indented as sub-menu */}
						{CURRENT_LOAN_STEPS.map((step) => {
							const rawStatus = loanGuid
								? getStepStatus(step.key)
								: step.key === "upload"
								? "active"
								: "pending";
							// Dashboard and Qualify are both navigable as soon as XML is uploaded
							// (loanGuid set) — Upload now lands on Dashboard first, so Qualify
							// must not stay locked behind the old "visit qualify via path-select
							// first" sequencing; the two are usable interchangeably once the
							// loan exists.
							// Qualify is checked only when its flow is finished (see isQualifyComplete);
							// until then it stays navigable ("open") but unchecked.
							const qualifyStatus = !loanGuid
								? "pending"
								: rawStatus === "active"
								? "active"
								: isQualifyComplete({ selectedPath, selectedProgram, qualifyInputs })
								? "done"
								: "open";
							const status =
								step.key === "qualify" && !incomeDocumentationType
									? "pending"
									: step.key === "qualify"
									? qualifyStatus
									: (step.key === "dashboard" || step.key === "qualify") &&
									  loanGuid &&
									  rawStatus === "pending"
									? "done"
									: rawStatus;
							const isActive = status === "active" || status === "open";
							const isDone = status === "done";
							const isPending = status === "pending";
							const canNavigate = !isPending;
							const isActiveRoute =
								pathname === step.path ||
								(step.pathSelect && pathname === step.pathSelect);
							return (
								<ListItem
									key={step.key}
									disablePadding
									className={`${styles.clSubItem} ${
										isActiveRoute ? styles.clSubItemActive : ""
									} ${isDone ? styles.clSubItemDone : ""} ${
										isPending ? styles.clSubItemPending : ""
									}`}
								>
									<ListItemButton
										disabled={isPending}
										onClick={() => canNavigate && handleStepNavigate(step)}
										sx={{ pl: 7, pt: 1.5, pb: 1.5, pr: 1.5 }}
									>
										<ListItemIcon>
											{isDone ? (
												<CheckBox className={styles.clIconDone} />
											) : (
												<CheckBoxOutlineBlank
													className={
														isActive ? styles.clIconActive : styles.clIconPending
													}
												/>
											)}
										</ListItemIcon>
										<Heading5
											text={step.label}
											fontType="semibold"
											extraClass={
												isDone
													? styles.clStepTextDone
													: isActive
													? styles.clStepTextActive
													: styles.clStepTextPending
											}
										/>
									</ListItemButton>
								</ListItem>
							);
						})}
					</List>
					<Divider className={styles.dividerLine} />
				</>
			)}

			{/* Loan Upload History, Loan Pipeline, Quick Pricer, Resources … */}
			<List className={styles.drawerList}>
				{drawerTopData?.slice(2).map((item, index) => {
					if (shouldRenderItem(item)) {
						const hasSubItems = item.subItems && item.subItems.length > 0;
						const isExpanded = expandedItems[item.title];
						const isSelected = isCurrentPath(item.url);

						return (
							<React.Fragment key={`rest-${index}`}>
								<ListItem
									disablePadding
									className={
										isItemDisabled(item)
											? styles.drawerDisabledItem
											: isSelected
												? styles.drawerSelectedItem
												: styles.drawerItem
									}
								>
									<ListItemButton
										onClick={() => {
											if (!isItemDisabled(item)) {
												handleNavWithGuard(item.title, item.path);
											}
										}}
									>
										<ListItemIcon>{item.icon}</ListItemIcon>
										<Heading5
											text={item.title}
											fontType="semibold"
											extraClass={
												isSelected
													? styles.drawerSelectedText
													: styles.drawerText
											}
										/>
										{hasSubItems && (
											<Box
												sx={{ marginLeft: "auto", cursor: "pointer" }}
												onClick={(e) => {
													e.stopPropagation();
													handleExpandClick(item.title);
												}}
											>
												{isExpanded ? <ExpandLess /> : <ExpandMore />}
											</Box>
										)}
									</ListItemButton>
								</ListItem>
								{hasSubItems && (
									<Collapse in={isExpanded} timeout="auto" unmountOnExit>
										<List component="div" disablePadding>
											{item.subItems.map((subItem, subIndex) => (
												<ListItem
													key={`${index}-${subIndex}`}
													disablePadding
													className={
														isCurrentPath(subItem.url)
															? styles.drawerSelectedItem
															: styles.drawerItem
													}
													sx={{ pl: 4 }}
												>
													<ListItemButton
														onClick={() =>
															handleNavWithGuard(subItem.title, subItem.path)
														}
													>
														<ListItemIcon sx={{ minWidth: "32px" }}>
															{subItem.icon}
														</ListItemIcon>
														<Heading5
															text={subItem.title}
															fontType="semibold"
															extraClass={
																isCurrentPath(subItem.url)
																	? styles.drawerSelectedText
																	: styles.drawerText
															}
														/>
													</ListItemButton>
												</ListItem>
											))}
										</List>
									</Collapse>
								)}
							</React.Fragment>
						);
					}
					return null;
				})}
			</List>
			<List className={`${styles.logoutContainer} ${styles.drawerList}`}>
				{userDetails && (
					<ListItem disablePadding className={styles.drawerItem}>
						<ListItemButton onClick={handleLogout}>
							<ListItemIcon>
								<LogoutOutlinedIcon sx={{ fontSize: "32px" }} />
							</ListItemIcon>
							<Heading5
								text="Log Out"
								fontType="semibold"
								extraClass={styles.drawerText}
							/>
						</ListItemButton>
					</ListItem>
				)}
				{userDetails?.user_roles?.includes("admin") &&
					userDetails &&
					drawerBottomData?.map((item, index) => (
						<ListItem
							key={index}
							disablePadding
							className={
								isCurrentPath(item.url)
									? styles.drawerSelectedItem
									: styles.drawerItem
							}
						>
							<ListItemButton
								onClick={() => handleNavWithGuard(item.title, item.path)}
							>
								<ListItemIcon>{item.icon}</ListItemIcon>
								<Heading5
									text={item.title}
									fontType="semibold"
									extraClass={
										isCurrentPath(item.url)
											? styles.drawerSelectedText
											: styles.drawerText
									}
								/>
							</ListItemButton>
						</ListItem>
					))}
			</List>
			<List className={`${styles.helpContainer} ${styles.drawerList}`}>
				<List disablePadding className={styles.drawerItem}>
					<ListItemButton onClick={() => setHelpCenterOpened(true)}>
						<ListItemIcon>
							<HelpOutlineOutlined />
						</ListItemIcon>
						<Heading5
							text="Help Center"
							fontType="semibold"
							extraClass={styles.drawerText}
						/>
					</ListItemButton>
				</List>
			</List>
		</Box>
	);

	return (
		<Box
			component="nav"
			sx={{
				width: { sm: drawerWidth },
				flexShrink: { sm: 0 },
			}}
			aria-label="mailbox folders"
		>
			<Drawer
				variant="temporary"
				open={mobileOpen}
				onClose={handleDrawerToggle}
				ModalProps={{
					keepMounted: true, // Better open performance on mobile.
				}}
				sx={{
					display: { xs: "block", sm: "none" },
					"& .MuiDrawer-paper": {
						boxSizing: "border-box",
						width: drawerWidth,
					},
				}}
				className={styles.drawerMobileContainer}
			>
				{drawer}
			</Drawer>
			<Drawer
				variant="permanent"
				sx={{
					display: { xs: "none", sm: "block" },
					"& .MuiDrawer-paper": {
						boxSizing: "border-box",
						width: drawerWidth,
					},
				}}
				open
				className={styles.drawerContainer}
			>
				{drawer}
			</Drawer>
			<HelpCenterModal
				open={helpCenterOpened}
				showHelpCentre={setHelpCenterOpened}
			/>
			<ConfirmationModal
				open={!!unsavedGuardPath}
				text={VERBIAGE_CONSTANTS.UNSAVED_DASHBOARD_CHANGES}
				confirmText={VERBIAGE_CONSTANTS.UNSAVED_DASHBOARD_SAVE}
				handleConfirm={handleUnsavedSave}
				closeText={VERBIAGE_CONSTANTS.UNSAVED_DASHBOARD_STAY}
				handleClose={() => setUnsavedGuardPath(null)}
			/>
			<ConfirmationModal
				open={navGuardOpen}
				text="Your progress has been saved. You can return to this loan from the Current Loan menu at any time."
				confirmText="Save & leave"
				handleConfirm={handleGuardConfirm}
				closeText="Stay here"
				handleClose={handleGuardCancel}
			/>
		</Box>
	);
};

export default CustomDrawer;
